import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './BibaChatAI.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * BibaChatAI Component
 * ChatGPT-like interface for interacting with OpenAI using Responses API
 * 
 * Features:
 * - Stateless conversation (no MongoDB storage)
 * - Client-side conversation history management
 * - Local storage for persistence across sessions
 * - Support for multiple chat threads
 * - File upload support (images and documents)
 */
const BibaChatAI = () => {
    const [sessionId, setSessionId] = useState(null);
    const [threads, setThreads] = useState([]); // Stored in localStorage
    const [currentThreadId, setCurrentThreadId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [initializing, setInitializing] = useState(true);
    const [error, setError] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [checking, setChecking] = useState(true);
    const messagesEndRef = useRef(null);
    const messagesContainerRef = useRef(null);
    const fileInputRef = useRef(null);
    const navigate = useNavigate();

    // Load threads from localStorage on mount
    useEffect(() => {
        const savedThreads = localStorage.getItem('bibaChatThreads');
        if (savedThreads) {
            try {
                const parsedThreads = JSON.parse(savedThreads);
                setThreads(parsedThreads);
            } catch (err) {
                console.error('Error parsing saved threads:', err);
                setThreads([]);
            }
        }
    }, []);

    // Save threads to localStorage whenever they change
    useEffect(() => {
        if (threads.length > 0) {
            localStorage.setItem('bibaChatThreads', JSON.stringify(threads));
        }
    }, [threads]);

    // Load current thread's messages when thread changes
    useEffect(() => {
        if (currentThreadId) {
            const thread = threads.find(t => t.id === currentThreadId);
            if (thread) {
                setMessages(thread.messages || []);
            }
        }
    }, [currentThreadId, threads]);

    // Format date and time for thread title
    const formatThreadTitle = (dateString) => {
        try {
            const date = dateString ? new Date(dateString) : new Date();
            if (isNaN(date.getTime())) {
                return new Date().toLocaleString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                });
            }
            return date.toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            return new Date().toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        }
    };

    // Check authentication and initialize session
    useEffect(() => {
        const token = localStorage.getItem('adminToken');
        if (token) {
            setIsAuthenticated(true);
            initializeSession();
        } else {
            navigate('/admin/login');
        }
        setChecking(false);
    }, [navigate]);

    // Initialize session
    const initializeSession = async () => {
        try {
            setInitializing(true);
            setError(null);
            const token = localStorage.getItem('adminToken');
            
            const response = await axios.post(
                `${API_URL}/biba-chat-ai/sessions`,
                {},
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            setSessionId(response.data.sessionId);
            
            // If no threads exist, create a new one
            if (threads.length === 0) {
                createNewThread();
            } else {
                // Load the most recent thread
                const mostRecentThread = threads[0];
                setCurrentThreadId(mostRecentThread.id);
            }
            
            setInitializing(false);
        } catch (err) {
            console.error('Error initializing session:', err);
            setError(err.response?.data?.error || 'Failed to initialize chat session');
            setInitializing(false);
        }
    };

    // Create a new thread
    const createNewThread = () => {
        const newThreadId = `thread_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const newThread = {
            id: newThreadId,
            title: formatThreadTitle(new Date()),
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            messages: [],
            openaiResponseId: null  // tracks the last OpenAI response ID for server-side threading
        };
        
        setThreads(prevThreads => [newThread, ...prevThreads]);
        setCurrentThreadId(newThreadId);
        setMessages([]);
        setError(null);
    };

    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages, loading]);

    // Handle file selection
    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        const validFiles = files.filter(file => {
            const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 
                               'application/pdf', 'text/plain', 'application/msword', 
                               'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
            return validTypes.includes(file.type);
        });
        
        if (validFiles.length !== files.length) {
            setError('Some files were not added. Only images (jpg, png, gif, webp) and documents (pdf, txt, doc, docx) are allowed.');
        }
        
        setSelectedFiles(prev => [...prev, ...validFiles]);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Handle paste event for screenshots/images
    const handlePaste = async (e) => {
        const items = e.clipboardData?.items;
        if (!items) return;

        const imageFiles = [];
        const timestamp = Date.now();
        
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            
            if (item.type.indexOf('image') !== -1) {
                const blob = item.getAsFile();
                if (blob) {
                    const extension = blob.type.split('/')[1] || 'png';
                    const fileName = `pasted-image-${timestamp}-${i}.${extension}`;
                    const file = new File([blob], fileName, { type: blob.type });
                    imageFiles.push(file);
                }
            }
        }

        if (imageFiles.length > 0) {
            e.preventDefault();
            setSelectedFiles(prev => [...prev, ...imageFiles]);
        }
    };

    // Remove a file from selection
    const removeFile = (index) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    };

    // Send message — conversation threading is maintained server-side via previousResponseId
    const sendMessage = async (e) => {
        e.preventDefault();
        if ((!inputMessage.trim() && selectedFiles.length === 0) || loading || !sessionId || !currentThreadId) return;

        const messageContent = inputMessage.trim();
        const filesToSend = [...selectedFiles];
        setInputMessage('');
        setSelectedFiles([]);
        setLoading(true);
        setError(null);

        const userMessage = {
            role: 'user',
            content: messageContent,
            files: filesToSend.map(f => ({ name: f.name, type: f.type, size: f.size })),
            timestamp: new Date().toISOString()
        };
        
        const newMessages = [...messages, userMessage];
        setMessages(newMessages);

        // Grab the last OpenAI response ID for this thread so the server can continue the conversation
        const currentThread = threads.find(t => t.id === currentThreadId);
        const previousResponseId = currentThread?.openaiResponseId || null;

        try {
            const token = localStorage.getItem('adminToken');
            let requestData;
            let headers = { Authorization: `Bearer ${token}` };
            
            if (filesToSend.length > 0) {
                requestData = new FormData();
                requestData.append('message', messageContent || '');
                if (previousResponseId) {
                    requestData.append('previousResponseId', previousResponseId);
                }
                filesToSend.forEach((file) => {
                    requestData.append('files', file);
                });
            } else {
                requestData = {
                    message: messageContent,
                    previousResponseId: previousResponseId
                };
                headers['Content-Type'] = 'application/json';
            }
            
            const response = await axios.post(
                `${API_URL}/biba-chat-ai/chat`,
                requestData,
                { headers }
            );

            // Extract assistant response content
            let assistantContent = '';
            if (response.data.response) {
                if (response.data.response.content) {
                    assistantContent = typeof response.data.response.content === 'string'
                        ? response.data.response.content
                        : response.data.response.content[0]?.text || JSON.stringify(response.data.response.content);
                } else if (response.data.response.message) {
                    assistantContent = response.data.response.message;
                } else {
                    assistantContent = JSON.stringify(response.data.response);
                }
            }

            const assistantMessage = {
                role: 'assistant',
                content: assistantContent || 'I received your message, but the response format was unexpected.',
                timestamp: new Date().toISOString()
            };
            
            const updatedMessages = [...newMessages, assistantMessage];
            setMessages(updatedMessages);

            // Persist new messages and the latest OpenAI response ID for this thread
            const newResponseId = response.data.responseId || null;
            setThreads(prevThreads => prevThreads.map(thread => {
                if (thread.id === currentThreadId) {
                    return {
                        ...thread,
                        messages: updatedMessages,
                        updatedAt: new Date().toISOString(),
                        openaiResponseId: newResponseId || thread.openaiResponseId
                    };
                }
                return thread;
            }));
        } catch (err) {
            console.error('Error sending message:', err);
            setError(err.response?.data?.error || 'Failed to send message. Please try again.');
            setMessages(prev => prev.slice(0, -1));
        } finally {
            setLoading(false);
        }
    };

    // Format timestamp for display
    const formatTime = (timestamp) => {
        const date = new Date(timestamp);
        const now = new Date();
        const diffInHours = (now - date) / (1000 * 60 * 60);

        if (diffInHours < 24) {
            return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
        } else if (diffInHours < 168) {
            return date.toLocaleDateString('en-US', { weekday: 'short', hour: '2-digit', minute: '2-digit' });
        } else {
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        }
    };

    // Handle new chat
    const handleNewChat = () => {
        createNewThread();
    };

    // Handle thread selection
    const handleThreadSelect = (threadId) => {
        if (threadId === currentThreadId) return;
        setCurrentThreadId(threadId);
        setError(null);
    };

    // Handle thread deletion
    const handleDeleteThread = (threadIdToDelete, e) => {
        e.stopPropagation();
        
        if (!window.confirm('Are you sure you want to delete this thread?')) {
            return;
        }

        setThreads(prevThreads => prevThreads.filter(t => t.id !== threadIdToDelete));

        // If the deleted thread is the current one, create a new one
        if (threadIdToDelete === currentThreadId) {
            createNewThread();
        }
    };

    // Handle logout
    const handleLogout = () => {
        localStorage.removeItem('adminToken');
        navigate('/admin/login');
    };

    if (checking) {
        return (
            <div className="biba-chat-ai-container">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Checking authentication...</p>
                </div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return null;
    }

    return (
        <div className="biba-chat-ai-container">
            <div className="biba-chat-ai-header">
                <button onClick={() => navigate('/landing')} className="back-btn">
                    ← Back to Landing
                </button>
                <div className="header-center">
                    <button onClick={handleLogout} className="logout-btn">Logout</button>
                    <h1>BibaChatAI</h1>
                </div>
                <div className="header-actions">
                    <button onClick={handleNewChat} className="new-chat-btn" disabled={initializing}>
                        + New Chat
                    </button>
                </div>
            </div>

            {error && (
                <div className="error-banner">
                    <span>{error}</span>
                    <button onClick={() => setError(null)} className="close-error-btn">×</button>
                </div>
            )}

            <div className="biba-chat-ai-layout">
                {/* Threads Sidebar */}
                <div className="threads-sidebar">
                    <div className="threads-header">
                        <h2>Chats</h2>
                    </div>
                    <div className="threads-list">
                        {threads.length === 0 ? (
                            <div className="threads-empty">
                                <p>No chats yet</p>
                                <p className="threads-empty-hint">Start a new conversation</p>
                            </div>
                        ) : (
                            threads.map((thread) => (
                                <div
                                    key={thread.id}
                                    className={`thread-item ${thread.id === currentThreadId ? 'active' : ''}`}
                                    onClick={() => handleThreadSelect(thread.id)}
                                >
                                    <div className="thread-content">
                                        <div className="thread-title">{thread.title || 'New Chat'}</div>
                                    </div>
                                    <button
                                        className="thread-delete-btn"
                                        onClick={(e) => handleDeleteThread(thread.id, e)}
                                        title="Delete thread"
                                    >
                                        ×
                                    </button>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Main Chat Area */}
                <div className="biba-chat-ai-content">
                {initializing ? (
                    <div className="loading-container">
                        <div className="loading-spinner"></div>
                        <p>Initializing chat session...</p>
                    </div>
                ) : !sessionId ? (
                    <div className="error-container">
                        <p>Failed to initialize chat session. Please try again.</p>
                        <button onClick={initializeSession} className="retry-btn">
                            Retry
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="chat-messages" ref={messagesContainerRef}>
                            {messages.length === 0 ? (
                                <div className="chat-welcome">
                                    <h2>Welcome to BibaChatAI</h2>
                                    <p>Start a conversation with the AI agent</p>
                                    <div className="suggestions">
                                        <button 
                                            className="suggestion-btn"
                                            onClick={() => setInputMessage("Hello! How can you help me today?")}
                                        >
                                            👋 Say hello
                                        </button>
                                        <button 
                                            className="suggestion-btn"
                                            onClick={() => setInputMessage("What can you do?")}
                                        >
                                            ❓ What can you do?
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                messages.map((msg, index) => (
                                    <div key={index} className={`message ${msg.role}`}>
                                        <div className="message-avatar">
                                            {msg.role === 'user' ? '👤' : '🤖'}
                                        </div>
                                        <div className="message-content">
                                            {msg.content && (
                                                <div className="message-text">{msg.content}</div>
                                            )}
                                            {msg.files && msg.files.length > 0 && (
                                                <div className="message-files">
                                                    {msg.files.map((file, fileIndex) => (
                                                        <div key={fileIndex} className="message-file">
                                                            <span className="file-icon">
                                                                {file.type?.startsWith('image/') ? '🖼️' : '📄'}
                                                            </span>
                                                            <span className="file-name">{file.name}</span>
                                                            <span className="file-size">
                                                                {file.size ? `(${(file.size / 1024).toFixed(1)} KB)` : ''}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            <div className="message-time">{formatTime(msg.timestamp)}</div>
                                        </div>
                                    </div>
                                ))
                            )}
                            {loading && (
                                <div className="message assistant">
                                    <div className="message-avatar">🤖</div>
                                    <div className="message-content">
                                        <div className="message-text typing-indicator">
                                            <span></span><span></span><span></span>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div ref={messagesEndRef} />
                        </div>

                        {selectedFiles.length > 0 && (
                            <div className="selected-files">
                                {selectedFiles.map((file, index) => (
                                    <div key={index} className="selected-file">
                                        <span className="file-icon">
                                            {file.type.startsWith('image/') ? '🖼️' : '📄'}
                                        </span>
                                        <span className="file-name" title={file.name}>
                                            {file.name.length > 20 ? file.name.substring(0, 20) + '...' : file.name}
                                        </span>
                                        <button 
                                            type="button"
                                            className="remove-file-btn"
                                            onClick={() => removeFile(index)}
                                            title="Remove file"
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                        <form onSubmit={sendMessage} className="chat-input-form">
                            <div className="input-container">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    multiple
                                    accept="image/*,.pdf,.txt,.doc,.docx"
                                    onChange={handleFileSelect}
                                    className="file-input"
                                    id="file-input"
                                    style={{ display: 'none' }}
                                />
                                <label htmlFor="file-input" className="attach-btn" title="Attach file">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path>
                                    </svg>
                                </label>
                                <textarea
                                    rows={3}
                                    value={inputMessage}
                                    onChange={(e) => setInputMessage(e.target.value)}
                                    onPaste={handlePaste}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                            e.preventDefault();
                                            if (!loading && sessionId && (inputMessage.trim() || selectedFiles.length > 0)) {
                                                sendMessage(e);
                                            }
                                        }
                                    }}
                                    placeholder="Type your message... (or paste screenshots/images)"
                                    className="chat-input"
                                    disabled={loading || !sessionId}
                                    autoFocus
                                />
                                <button 
                                    type="submit" 
                                    className="send-btn" 
                                    disabled={loading || (!inputMessage.trim() && selectedFiles.length === 0) || !sessionId}
                                    title="Send message"
                                >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="22" y1="2" x2="11" y2="13"></line>
                                        <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                                    </svg>
                                </button>
                            </div>
                        </form>
                    </>
                )}
                </div>
            </div>
        </div>
    );
};

export default BibaChatAI;
